import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { orderedVisibleSections, siteConfig, type SectionId } from './siteConfig';
import { Header } from './components/Header';
import { Footer } from './components/Footer';
import { Hero } from './sections/Hero';
import { Program } from './sections/Program';
import { Venue } from './sections/Venue';
import { Accommodation } from './sections/Accommodation';
import { Rsvp } from './sections/Rsvp';
import { DressCode } from './sections/DressCode';
import { Gift } from './sections/Gift';
import { Gallery } from './sections/Gallery';
import { Contact } from './sections/Contact';
import { loadUser } from './pexesoStore';

const SECTION_COMPONENTS: Record<Exclude<SectionId, 'hero' | 'rsvp'>, React.ComponentType> = {
  program: Program,
  venue: Venue,
  accommodation: Accommodation,
  dressCode: DressCode,
  gift: Gift,
  gallery: Gallery,
  contact: Contact,
};

export default function App() {
  const { i18n } = useTranslation();

  /* Returning guest (name cookie present) skips the pexeso intro and goes
     straight to the unlocked layout — Rsvp + the nav link should be visible
     too. New guests unlock both once they finish or skip the game. */
  const [gameComplete, setGameComplete] = useState<boolean>(() => loadUser() !== null);
  const handleGameComplete = () => setGameComplete(true);

  useEffect(() => {
    document.documentElement.lang = i18n.language.split('-')[0] || 'cs';
  }, [i18n.language]);

  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty('--accent', siteConfig.theme.accent);
    root.style.setProperty('--accent-dark', siteConfig.theme.accentDark);
    root.style.setProperty('--text', siteConfig.theme.text);
    root.style.setProperty('--mute', siteConfig.theme.mute);
    root.style.setProperty('--bg', siteConfig.theme.bg);
  }, []);

  const visible = orderedVisibleSections();

  return (
    <>
      <Header gameComplete={gameComplete} />
      <main>
        {visible.map((id) => {
          if (id === 'hero') return <Hero key={id} onGameComplete={handleGameComplete} />;
          if (id === 'rsvp') return gameComplete ? <Rsvp key={id} /> : null;
          const Component = SECTION_COMPONENTS[id];
          return <Component key={id} />;
        })}
      </main>
      <Footer />
    </>
  );
}
